import type { TFunction } from "i18next";
import type { ChangeReason, VersionBump, VersionLabel } from "../../../api/content/versionDecoders";
import { formatDateTime } from "../../../shared/formatting/dateTimes";
import type { PlanRow } from "../merge/versionConfirmation";

/**
 * How a version reads on screen (ADR-0033 D1): by name and semantic version, such as
 * "Extract claims 1.2.0". The exact reference is never the main text.
 */

/** "Extract claims 1.2.0"; the project content as a whole is "Project content 1.3.0". */
export function versionLabelText(t: TFunction, name: string | null, version: string): string {
  return t("versions.label", { name: name ?? t("versions.projectContent"), version });
}

/** A version in a list of choices: its tags (such as "Newest"), label, change line and date. */
export function versionOptionText(t: TFunction, name: string | null, label: VersionLabel, tags: readonly string[], language: string | undefined): string {
  return [...tags, versionLabelText(t, name, label.version), label.changeLine, formatDateTime(label.labelledAt, language)].filter((part) => part !== "").join(" · ");
}

export function bumpText(t: TFunction, bump: VersionBump): string {
  return t(`versions.bumps.${bump}`);
}

/** Each way the subject changed, in words, once each: "steps added and prompt reworded". */
export function reasonsText(t: TFunction, reasons: readonly ChangeReason[], language: string | undefined): string {
  const texts = [...new Set(reasons)].map((reason) => t(`versions.reasons.${reason}`));
  return new Intl.ListFormat(language, { type: "conjunction" }).format(texts);
}

/** A merge row's state: "Draft based on 1.2.0 · proposes 1.3.0 · steps added". */
export function planRowText(t: TFunction, row: PlanRow, language: string | undefined): string {
  switch (row.status) {
    case "bump": {
      const draft = t("versions.merge.draft", { current: row.current, proposed: row.proposed });
      return row.reasons.length === 0 ? draft : `${draft} · ${reasonsText(t, row.reasons, language)}`;
    }
    case "new":
      return t("versions.merge.new");
    case "removed":
      return t("versions.merge.removed");
    case "unchanged":
      return t("versions.merge.unchanged", { current: row.current });
  }
}
